import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MenuItem, MenuList, Tab, Tabs } from "@mui/material";
import { exportView } from "../../../../navigation";
import {
	CircularLoader,
	CustomButton,
	CustomIconButton,
	CustomTooltip,
	DropDown,
	icon,
	IconComponent,
} from "../../../../common/components";
import { hasCompanyPermission, hasPermission, SegFlags, translate, Permissions } from "../../../../common/providers";
import styles from "./ProjectSheet.module.css";
import { debounce, downloadFile, isNonEmptyArray, isNonEmptyObject } from "../../../../common/utils";
import { useApi, useEffectOnce, useTrackEvent } from "../../../../common/hooks";
import { AiDetectionsService, ProjectDocumentService, connectSSE } from "../../../../api";
import { DrawerContent } from "./components/drawer-content/DrawerContent";
import { SectionContent } from "./components/section-content/SectionContent";
import { setSelectedProjectSheetView } from "../../../../common/slice";
import { FIELD_STATUS } from "./constants";
import {
	setGlobalSections,
	setInnerFields,
	setOngoingProgress,
	setPossibleValuesInnerFields,
} from "../slice/project-sheet-slice";

const SideDrawerOption = ({ launchInProgress, open, onOpen }) => {
	const handleOpen = () => {
		onOpen();
	};
	return (
		<div className={styles.sideDrawerOption}>
			{(launchInProgress && <div className={styles.disabledButton} />) || (
				<CustomIconButton
					className={styles.drawer__iconBtn}
					icon={(open && icon.faChevronLeft) || icon.faBars}
					size="sm"
					variant="outlined"
					onClick={handleOpen}
				/>
			)}
		</div>
	);
};

function StyledTabs({ children, onChange, value }) {
	return (
		<Tabs
			classes={{
				root: styles.StyledTabs__root,
				indicator: styles.StyledTabs__indicator,
			}}
			scrollButtons="auto"
			textColor="primary"
			value={value}
			variant="scrollable"
			onChange={onChange}
		>
			{children}
		</Tabs>
	);
}

const debouncedFunction = debounce((func) => func(), 100);

const HIDE_LAUNCH = false;

function ProjectSheet() {
	const [refreshCounter, setRefreshCounter] = useState(0);
	const [launchProgress, setLaunchProgress] = useState(null);
	const [projectHasDocuments, setProjectHasDocuments] = useState(false);
	const [isLoadingViews, setIsLoadingViews] = useState(true);
	const [selectedTab, setSelectedtab] = useState(null);
	const [selectedSection, setSelectedSection] = useState(null);
	const [openDrawer, setOpenDrawer] = useState(true);
	const [detectedValues, setDetectedValues] = useState([]);
	const [views, setViews] = useState([]);
	const [isExecutingAgent, setIsExecutingAgent] = useState(false);
	const [isLaunchButtonActive, setIsLaunchButtonActive] = useState(true);
	const [isExportActive, setIsExportActive] = useState(false);
	const [hasLaunchFinished, setHasLaunchFinished] = useState(false);
	const [detectionUp, setDetectionUp] = useState(false);
	const [openExportDropdown, setOpenExportDropdown] = useState(false);
	const [templates, setTemplates] = useState([]);
	const [isExporting, setIsExporting] = useState(false);
	const sections = useSelector(({ projectSheet }) => projectSheet.globalSections);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const cautionBannerDisabled = useSelector(({ context }) => context.cautionBannerHidden);
	const selectedProjectSheetView = useSelector(({ context }) => context?.selectedProjectSheetView);
	const { call: getPageList } = useApi(AiDetectionsService.getPageList);
	const { call: getDetectionPageTemplate } = useApi(AiDetectionsService.getDetectionPageTemplate);
	const { call: getProjectHasDocuments } = useApi(ProjectDocumentService.projectHasDocuments);
	const { call: getFieldsValues } = useApi(AiDetectionsService.getFieldsValues);
	const { call: updateFieldStatus } = useApi(AiDetectionsService.updateFieldStatus);
	const { call: exportPage } = useApi(AiDetectionsService.exportPage);
	const { call: executeAgentOnPage } = useApi(AiDetectionsService.executeAgentOnPage);
	const { call: getIsAiDetectionsLaunchActive } = useApi(AiDetectionsService.getIsAiDetectionsLaunchActive);
	const { call: getCheckDetection } = useApi(AiDetectionsService.getCheckDetection);
	const { call: getInnerFields } = useApi(AiDetectionsService.getInnerFields);
	const { call: getInnerFieldPossibleValues } = useApi(AiDetectionsService.getInnerFieldPossibleValues);
	const { call: getTemplates } = useApi(AiDetectionsService.getTemplates);
	const exportOptionsRef = useRef(null);
	const trackEvent = useTrackEvent();
	const { call: getProgressSnapshot } = useApi(AiDetectionsService.getProgressSnapshot);
	const sseConnectionRef = useRef(null);
	const selectedPageId = useMemo(() => views[selectedTab]?.id, [views, selectedTab]);
	const hasRights =
		hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
		hasCompanyPermission([Permissions.PROJECT_LEADER]);
	const disabledLaunchButton = useMemo(
		() => HIDE_LAUNCH || isExecutingAgent || !isLaunchButtonActive || launchProgress || !hasRights,
		[isExecutingAgent, hasRights, launchProgress, isLaunchButtonActive]
	);
	const dispatch = useDispatch();
	useEffect(() => {
		const documentTitle = translate("navigation:project.my-review-criteria");
		document.title = documentTitle;
		trackEvent("Detection_visit");
		// eslint-disable-next-line
	}, []);
	useEffect(() => {
		getTemplates({ projectId })
			.then((data) => {
				const reordered = [
					...data.filter((item) => item.id === null),
					...data.filter((item) => item.id !== null),
				];
				setTemplates(reordered);
			})
			.catch(console.error);
	}, [getTemplates, projectId]);
	const handleSelectSection = useCallback((part, selSection) => {
		if (!part) {
			setSelectedSection(null);
			return;
		}
		if (part?.uuid !== selSection?.section) {
			const newIds = [];
			const browsePart = (section) => {
				if (isNonEmptyArray(section?.layout)) {
					section.layout.forEach((element) => {
						newIds.push(element.id);
					});
				}
				if (isNonEmptyArray(section?.subsection)) {
					section.subsection.forEach((sub) => browsePart(sub));
				}
			};
			browsePart(part);
			setSelectedSection({ ids: newIds, section: part?.uuid });
		}
	}, []);
	const getPage = useCallback(
		(selPageId, pId, selectedSect) => {
			if (selPageId && pId) {
				setIsLoadingViews(true);
				return getDetectionPageTemplate({ projectId: pId, pageId: selPageId })
					.then((data) => {
						const layout = JSON.parse(data?.layout);
						const newSections =
							(isNonEmptyObject(layout) && isNonEmptyArray(layout?.sections) && layout.sections) || [];
						dispatch(setGlobalSections(newSections));
						setSelectedSection(null);
						setDetectedValues([]);
						if (selectedSect) {
							setSelectedSection(selectedSect);
							trackEvent("Detection_First_Visit_After_Finish");
						} else {
							handleSelectSection(null, null);
						}
					})
					.catch(console.error)
					.finally(() => setIsLoadingViews(false));
			}
			return Promise.resolve();
		},
		[dispatch, getDetectionPageTemplate, handleSelectSection, trackEvent]
	);
	const handleGetFields = useCallback(() => {
		const payload = (isNonEmptyArray(selectedSection?.ids) && selectedSection?.ids) || [];
		if (projectId && selectedPageId) {
			debouncedFunction(() =>
				getFieldsValues({ projectId, pageId: selectedPageId }, payload)
					.then((data) => setDetectedValues(data))
					.catch(console.error)
			);
		}
	}, [getFieldsValues, projectId, selectedPageId, selectedSection]);
	const handleFinishProgress = useCallback(() => {
		setLaunchProgress(null);
		dispatch(setOngoingProgress(null));
		setIsLaunchButtonActive(false);
		setHasLaunchFinished(true);
		getPage(selectedPageId, projectId, selectedSection).finally(() => handleGetFields());
		trackEvent("Detection_Finish");
	}, [dispatch, getPage, handleGetFields, trackEvent, selectedPageId, projectId, selectedSection]);
	useEffect(() => {
		if (hasLaunchFinished || launchProgress) {
			setDetectionUp(true);
		}
	}, [hasLaunchFinished, launchProgress]);
	useEffect(() => {
		try {
			getInnerFields({ projectId }).then((data) => {
				dispatch(setInnerFields(data));
				if (isNonEmptyArray(data)) {
					data.forEach((innerField) => {
						getInnerFieldPossibleValues({ projectId, fieldUuid: innerField.fieldUuid })
							.then((pl) => dispatch(setPossibleValuesInnerFields(pl)))
							.catch(console.error);
					});
				}
			});
		} catch (err) {
			console.error(err);
		}
	}, [dispatch, getInnerFields, getInnerFieldPossibleValues, projectId]);
	const handleProgress = useCallback(
		({ pageId, progress, reason }) => {
			if (reason === "COMPLETED" && progress === 100) {
				handleFinishProgress();
			}
			if (reason === "IN_PROGRESS" && progress && pageId) {
				setLaunchProgress({ pageId: Number.parseInt(pageId, 10), progress });
				dispatch(setOngoingProgress({ pageId: Number.parseInt(pageId, 10), progress }));
			}
		},
		[handleFinishProgress, dispatch]
	);
	const handleAutomationMessage = useCallback(
		({ pageId, progress, reason, value }) => {
			if (pageId && reason) {
				handleProgress({ pageId, progress, reason });
			} else {
				setIsLaunchButtonActive(value);
			}
		},
		[handleProgress]
	);
	useEffect(() => {
		if (!projectId) {
			return null;
		}
		sseConnectionRef.current?.();
		const onMessage = (message) => {
			const { data } = message;
			handleAutomationMessage({ ...data.payload, reason: data.action });
		};
		const onError = (error) => {
			console.error(error);
		};
		sseConnectionRef.current = connectSSE(`ai-detection/projects/${projectId}/subscribe`, { onMessage, onError });
		return () => {
			sseConnectionRef.current?.();
		};
	}, [projectId, handleAutomationMessage]);
	useEffectOnce(
		() => {
			if (selectedPageId) {
				getProgressSnapshot({ projectId, pageId: selectedPageId })
					.then(({ value }) => {
						if (value && value !== "100%") {
							const payload = { pageId: selectedPageId, progress: value };
							setLaunchProgress(payload);
							dispatch(setOngoingProgress(payload));
						}
					})
					.catch(console.error);
			}
		},
		[selectedPageId],
		() => !!selectedPageId
	);
	useEffect(() => {
		if (hasRights) {
			getIsAiDetectionsLaunchActive({ projectId })
				.then((data) => {
					setIsLaunchButtonActive(data.value);
				})
				.catch(console.error);
		}
	}, [getIsAiDetectionsLaunchActive, projectId, hasRights]);
	useEffect(() => {
		if (projectId && selectedPageId) {
			getCheckDetection({ projectId, pageId: selectedPageId }).then(setIsExportActive).catch(console.error);
		}
	}, [getCheckDetection, projectId, selectedPageId]);
	useEffect(() => {
		getProjectHasDocuments({ projectId })
			.then((data) => {
				setProjectHasDocuments(data);
				if (data) {
					getPageList({ projectId })
						.then((sViews) => {
							setViews(sViews);
						})

						.catch(console.error);
				}
			})
			.finally(() => setIsLoadingViews(false))
			.catch(console.error);
	}, [getPageList, getProjectHasDocuments, projectId]);
	useEffect(
		() => {
			getPage(selectedPageId, projectId, null);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedPageId]
	);
	useEffect(() => {
		handleGetFields();
	}, [handleGetFields, refreshCounter]);
	useEffectOnce(
		() => {
			if (selectedProjectSheetView) {
				setSelectedtab(selectedProjectSheetView);
			} else {
				setSelectedtab(0);
			}
		},
		[selectedProjectSheetView, views],
		() => isNonEmptyArray(views)
	);
	const handleChangeTab = (_, newValue) => {
		setSelectedtab(newValue);
		const pageId = views[newValue]?.id;
		if (pageId) {
			getPage(pageId, projectId, null);
			dispatch(setSelectedProjectSheetView(newValue));
		}
	};
	const handleExport = (templateId) => {
		setIsExporting(true);
		setOpenExportDropdown(false);
		exportPage({ projectId, pageId: selectedPageId }, { templateId })
			.then(({ data, filename }) => {
				downloadFile({ data, filename, filetype: "xlsx" });
				trackEvent("Detection_Exports");
			})
			.catch(console.error)
			.finally(() => {
				setIsExporting(false);
			});
	};
	const handleLaunchDetection = () => {
		setIsExecutingAgent(true);
		executeAgentOnPage({ projectId, pageId: selectedPageId })
			.then(() => {
				setLaunchProgress({ pageId: selectedPageId, progress: 0 });
				dispatch(setOngoingProgress({ pageId: selectedPageId, progress: 0 }));
				trackEvent("Detection_Start");
				getPage(selectedPageId, projectId, null);
			})
			.catch(console.error)
			.finally(() => setIsExecutingAgent(false));
	};
	const handleOpenDrawer = () => {
		setOpenDrawer((prev) => !prev);
	};
	const handleValidate = (fieldUuid) => {
		const field =
			isNonEmptyArray(detectedValues) && detectedValues.find((detField) => detField.fieldUuid === fieldUuid);
		const newStatus = isNonEmptyArray(field?.values) ? field.values[0].status !== FIELD_STATUS.VALIDATED : true;
		if (projectId && fieldUuid && field) {
			updateFieldStatus({ projectId, fieldUuid }, newStatus)
				.then((data) => {
					setDetectedValues((prev) =>
						prev.map(
							(detVal) =>
								(detVal.fieldUuid === field.fieldUuid && {
									...detVal,
									values:
										(isNonEmptyArray(detVal.values) &&
											detVal.values.map(
												(val) =>
													({
														...val,
														status:
															(newStatus && FIELD_STATUS.VALIDATED) ||
															FIELD_STATUS.UNREVIEWED,
													} || val)
											)) ||
										data.values,
								}) ||
								detVal
						)
					);
					trackEvent("Detection_Validates_Criteria");
				})
				.catch(console.error);
		}
	};
	const handleRefreshSections = () => {
		setRefreshCounter((prev) => prev + 1);
	};
	const handleUpdateCompliance = (fieldUuid, values) => {
		setDetectedValues((prev) =>
			prev.map((detectedValue) => {
				if (detectedValue.fieldUuid !== fieldUuid) {
					return detectedValue;
				}
				const existingUuids = new Set(detectedValue.innerFields.map((inner) => inner.fieldUuid));
				const updatedInnerFields = detectedValue.innerFields.map((inner) => {
					const matchedValue = values.find((v) => v.fieldUuid === inner.fieldUuid);
					return matchedValue ? { ...inner, value: matchedValue.value } : inner;
				});
				const newInnerFields = values.filter((v) => !existingUuids.has(v.fieldUuid));
				return {
					...detectedValue,
					innerFields: [...updatedInnerFields, ...newInnerFields],
				};
			})
		);
	};
	const handleExportFromList = () => {
		if (templates.length > 1) {
			setOpenExportDropdown(true);
		} else {
			handleExport(templates?.[0]?.id);
		}
	};
	return (
		<>
			{(launchProgress || hasLaunchFinished) && (
				<div className={styles.progressBanner} data-has-finished={hasLaunchFinished}>
					{(launchProgress || hasLaunchFinished) && (
						<>
							<IconComponent
								color={(launchProgress && "var(--blue-500)") || "var(--lime-500)"}
								icon={icon[(launchProgress && "faSpinner") || "faCheckSquare"]}
								size="sm"
								spin={!!launchProgress}
							/>
							<div className={styles.progressBanner__text}>
								<div className={styles["progressBanner__text--title"]}>
									{(launchProgress &&
										translate("dashboards.project-sheet.launch-detection.ongoing", {
											progress:
												typeof launchProgress?.progress === "number"
													? `${launchProgress?.progress}%`
													: launchProgress?.progress,
										})) ||
										translate("dashboards.project-sheet.launch-detection.finished")}
								</div>
								<span>
									{translate(
										(launchProgress &&
											"dashboards.project-sheet.launch-detection.ongoing.message") ||
											"dashboards.project-sheet.launch-detection.finished.message"
									)}
								</span>
							</div>
							<CustomIconButton icon={icon.faXmark} onClick={() => setHasLaunchFinished(false)} />
						</>
					)}
				</div>
			)}
			{((selectedTab || selectedTab === 0) && isNonEmptyArray(views) && (
				<>
					<div className={styles.header}>
						<StyledTabs value={selectedTab} onChange={handleChangeTab}>
							{isNonEmptyArray(views) &&
								views.map((i) => (
									<Tab
										key={i.id}
										classes={{ root: styles.innerTabClassName }}
										className={styles.viewTab}
										label={
											<CustomTooltip title={i.name?.length > 17 ? translate(i.name) : ""}>
												{i.name?.length > 17 ? `${i.name?.substring(0, 16)}...` : i.name}
											</CustomTooltip>
										}
									/>
								))}
						</StyledTabs>
						<div className={styles["header__action-btn"]}>
							<CustomTooltip title={!hasRights && translate("common:permissions.insufficient-rights")}>
								<span ref={exportOptionsRef} className={styles.exportBtn}>
									<CustomButton
										className={styles["exportBtn__btn-main-left"]}
										color="primary"
										disabled={!!launchProgress || !hasRights || !isExportActive}
										size="sm"
										startIcon={!isExporting && icon.faDownload}
										variant="outlined"
										onClick={() => handleExport(templates?.[0]?.id)}
									>
										{isExporting && <CircularLoader size={14} />}
										<span>{translate("common:btn.export")}</span>
									</CustomButton>
									<CustomButton
										className={styles["exportBtn__btn-main-right"]}
										color="primary"
										disabled={!!launchProgress || !hasRights || !isExportActive}
										size="sm"
										startIcon={icon.faChevronDown}
										variant="outlined"
										onClick={handleExportFromList}
									/>
								</span>
							</CustomTooltip>
							<DropDown
								ref={exportOptionsRef}
								open={openExportDropdown}
								onClose={() => {
									setOpenExportDropdown(false);
								}}
							>
								<MenuList disablePadding>
									{templates.map(({ id, templateName }) => (
										<MenuItem
											key={id}
											className={styles.exportDropdown__menuItem}
											onClick={() => handleExport(id)}
										>
											<span className={styles.exportDropdown__title}>
												{(id === null &&
													translate("dashboards.project-sheet.templates.export.standard")) ||
													templateName}
											</span>
											<span className={styles.exportDropdown__description}>
												{(id === null &&
													translate(
														"dashboards.project-sheet.templates.export.standard.description"
													)) ||
													"-"}
											</span>
										</MenuItem>
									))}
								</MenuList>
							</DropDown>
							<CustomTooltip title={!hasRights && translate("common:permissions.insufficient-rights")}>
								<span>
									<CustomButton
										className={styles["headerAction--button__small"]}
										color="primary"
										disabled={!hasRights || disabledLaunchButton}
										size="sm"
										startIcon={icon.faWandMagic}
										variant="contained"
										onClick={handleLaunchDetection}
									>
										{translate("dashboards.project-sheet.launch-detection")}
									</CustomButton>
								</span>
							</CustomTooltip>
						</div>
					</div>
					{((isExportActive ||
						launchProgress ||
						hasLaunchFinished ||
						(detectionUp && isNonEmptyArray(sections))) && (
						<div
							className={styles.mainDisplay}
							data-cautionBannerDisabled={cautionBannerDisabled}
							data-is-detection-in-progress={!!launchProgress}
						>
							{(isNonEmptyArray(sections) && (
								<>
									<div className={styles.sideDrawer__main} data-open={openDrawer}>
										<SideDrawerOption
											launchInProgress={!!launchProgress}
											open={openDrawer}
											onOpen={handleOpenDrawer}
										/>
										<DrawerContent
											launchInProgress={!!launchProgress}
											open={openDrawer}
											sections={sections}
											selectedPageId={selectedPageId}
											selectedSection={selectedSection?.section}
											onSelect={(information) =>
												handleSelectSection(information, selectedSection)
											}
										/>
									</div>
									<div className={styles.siderDrawer__content} data-open={openDrawer}>
										<SectionContent
											detectedValues={detectedValues}
											launchInProgress={!!launchProgress}
											sections={sections}
											selectedPageId={selectedPageId}
											selectedSection={selectedSection?.section}
											onRefreshSections={handleRefreshSections}
											onUpdateCompliance={handleUpdateCompliance}
											onValidate={handleValidate}
										/>
									</div>
								</>
							)) || (
								<div className={styles["views__empty-state"]}>
									{translate("dashboards.project-sheet.empty-state.sections")}
								</div>
							)}
						</div>
					)) ||
						(hasRights && (
							<div className={styles["views__empty-state"]}>
								{translate("dashboards.project-sheet.empty-state.no-detection")}
							</div>
						))}
				</>
			)) || (
				<div className={styles["views__empty-state"]}>
					{(isLoadingViews && <CircularLoader />) ||
						translate(
							(projectHasDocuments && "dashboards.project-sheet.empty-state.views") ||
								"dashboards.project-sheet.empty-state.documents"
						)}
				</div>
			)}
		</>
	);
}

export { default as projectSheetSlice } from "../slice/project-sheet-slice";
export default exportView({
	path: "/projects/:projectId/review/project-sheet",
	localesPath: "/dashboards/dashboard-project/locales",
	component: ProjectSheet,
	segFlag: SegFlags.AI_CRITERIA_DETECTION,
});
